// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useEffect, useMemo } from 'react';
import { useLocalMessage } from '@Helpers/';
import {
    ObservableOutput,
    ObjectTypeV1 as ObjectType,
    OrgPermissions,
    AdminPermissions,
    AnalyzerOutput,
    PermissionsEnum,
} from 'thehive-sdk';
import { useHistory } from 'react-router-dom';
import { ConfigList } from '@Hooks/useConfigList';
import { ObservableListTitle } from '@Organisms/Observable/ObservableListTitle/ObservableListTitle';
import { ObservableCreate } from '@Organisms/Observable/ObservableCreate/ObservableCreate';
import ObservableStats from '@Organisms/Observable/ObservableStats/ObservableStats';
import { actionList } from '@Organisms/Observable/actionList';
import { ObservableListStatus } from '@Organisms/Observable/ObservableListStatus/ObservableListStatus';
import FormatMultipleDate from '@Molecules/Format/FormatMultipleDate/FormatMultipleDate';
import ActionList from '@Molecules/ActionList/ActionList';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { SelectedInfo } from '@Atoms/SelectedInfo/SelectedInfo';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import { ObservableEventList } from '@Organisms/Observable/eventList';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { usePin } from '@Molecules/Pin';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import useDisplayParams from '@Molecules/DisplayParams/DisplayParams';
import { useState } from 'react';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { StatusContext } from '@Context/StatusProvider';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { ICONS } from '@Constants/iconsApp';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { useDrawer } from '@Hooks/useDrawer';
import { ObservableDrawer } from '../ObservableDrawer/ObservableDrawer';
import { MultipleDisplayEntityInfo } from '@Molecules/MultipleDisplayEntityInfo/MultipleDisplayEntityInfo';
import { useCurrentUser } from '@Queries/userQueries';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { Badge } from 'antd';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';

import './ObservableList.scss';

interface ObservableListProps {
    config: ConfigList<ObservableOutput>;
    eventList: ObservableEventList;
    analyzer: {
        data: AnalyzerOutput[] | undefined;
        onFinish: (items: any[]) => void;
    };
    caseId?: string;
    alertId?: string;
    permissions?: AdminPermissions[] | OrgPermissions[];
}

export const ObservableList = ({
    config,
    eventList,
    analyzer,
    caseId,
    alertId,
    permissions = [],
}: ObservableListProps): ReactElement => {
    const i = useLocalMessage();

    const history = useHistory();
    const pinContext = usePin();
    const { hasCortex, config: statusConfig } = useContext(StatusContext);
    const { visible, setVisible, data, setData } = useDrawer<ObservableOutput>({});
    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer<ObservableOutput>({});

    const currentType = caseId ? 'case' : 'alert';
    const currentId = caseId ? caseId : alertId;

    const { data: currentUser } = useCurrentUser();
    const { organisations = [] } = currentUser || {};

    const {
        storagePageManager,
        storageViewManager,
        dataSource = [],
        status = 'loading',
        columnsRenderer,
        rowSelectionCustomRenderer,
        listRefetch,
        countRefetch,
        filterManager,
        paginationManager,
        stateNeedRefresh: { needRefresh, setNeedRefresh },
        table,
        stateSelectedKeys: { selectedKeys },
        quickFilters,
    } = config;

    const { setDefaultDisplay } = storageViewManager || {};

    useStreamObserver({ objectType: STREAM_EVENTS.ANALYZER }, (data) => {
        const isRightParent = data.some(
            (action) =>
                (action.base.context?._type?.toUpperCase() === currentType.toUpperCase() &&
                    action.base.context?._id === currentId) ||
                (action.base.context?.[currentType] && action.base.context?.[currentType]._id === currentId),
        );

        if (!isRightParent) {
            return false;
        }

        const successFound = data.some(
            (action) => action.base.action === 'update' && action.base.details.status === 'Success',
        );

        if (successFound) {
            listRefetch();
        }
    });

    const {
        page: storagePage,
        setAutoRefresh,
        setStatsIsOpen,
        setFilterIsOpen,
        setDisplay,
        setOnLoadView,
    } = storagePageManager || { page: null };

    const hasPermissions = useSplitPermissions(permissions, [PermissionsEnum.MANAGE_OBSERVABLE]);

    const [showDates, DisplayDates, loadVisibleStateDates] = useDisplayParams({
        startDate: { label: i('common.start-date'), visible: storagePage?.display?.startDate ?? true },
        createdAt: { label: i('common.created-date'), visible: storagePage?.display?.createdAt ?? true },
        updatedAt: { label: i('common.updated-date'), visible: storagePage?.display?.updatedAt ?? true },
    });

    const [showTitle, DisplayTitle, loadVisibleStateTitle] = useDisplayParams({
        tags: { label: i('common.tags'), visible: storagePage?.display?.tags ?? true },
        reports: { label: i('common.reports'), visible: storagePage?.display?.reports ?? true },
    });

    const [localAutoRefresh, setLocalAutoRefresh] = useState(storagePage?.autoRefresh || false);
    const [localStatsIsOpen, setLocalStatsIsOpen] = useState(storagePage?.statsIsOpen || false);
    const [localFilterIsOpen, setLocalFilterIsOpen] = useState(storagePage?.filterIsOpen || false);

    setOnLoadView?.((viewContent) => {
        setLocalAutoRefresh(viewContent?.autoRefresh || false);
        setLocalStatsIsOpen(viewContent?.statsIsOpen || false);
        loadVisibleStateDates({
            startDate: viewContent?.display?.startDate ?? true,
            createdAt: viewContent?.display?.createdAt ?? true,
            updatedAt: viewContent?.display?.updatedAt ?? true,
        });

        loadVisibleStateTitle({
            tags: viewContent?.display?.tags ?? true,
            reports: viewContent?.display?.reports ?? true,
        });
    });

    const observableActionList = useMemo(
        () =>
            actionList(
                i,
                history,
                caseId ? ObjectType.CASE : ObjectType.ALERT,
                caseId || '',
                pinContext,
                analyzer,
                hasCortex,
                eventList,
                statusConfig,
                permissions,
            ),
        [i, history, caseId, pinContext, analyzer, hasCortex, eventList, statusConfig, permissions],
    );

    const columns = columnsRenderer([
        {
            title: i('common.flags'),
            className: 'observable-list__column-status',
            render: (
                { tlp, pap, ioc, sighted, extraData, ignoreSimilarity = false },
                { custom: filterCustom, ioc: filterIoc, sighted: filterSighted, bool: filterBool },
            ): ReactElement => (
                <ObservableListStatus
                    values={{
                        tlp,
                        ioc,
                        pap,
                        sighted,
                        seen: extraData?.seenSummary?.seen,
                        ignoreSimilarity,
                        seenIoc: !!extraData?.seenSummary?.ioc,
                        seenEstimate: extraData?.seenSummary?.estimate,
                    }}
                    onStatusClick={{
                        onTLPClick: (): void => filterCustom({ field: 'tlp', operator: 'any', values: [tlp] }),
                        onPAPClick: (): void => filterCustom({ field: 'pap', operator: 'any', values: [pap] }),
                        onIOCClick: (): void => filterIoc(ioc),
                        onSightedClick: (): void => filterSighted(sighted),
                        onSimilarityClick: (): void => filterBool('ignoreSimilarity', ignoreSimilarity + ''),
                    }}
                />
            ),
        },
        {
            title: [
                { label: i('common.data-type'), sortField: 'dataType' },
                { label: i('observable.list.table.columns.value'), sortField: 'data' },
            ],
            className: 'observable-list__column-name column_preview',
            optionalRender: DisplayTitle,
            render: (observable, { tags: filterTags, dataType: filterDataType }): ReactElement => (
                <ObservableListTitle
                    data={observable}
                    eventList={eventList}
                    onTagClick={filterTags}
                    reportsIsOpen={showTitle?.reports}
                    tagsIsOpen={showTitle?.tags}
                    entityId={currentId}
                    entityType={caseId ? ObjectType.CASE : ObjectType.ALERT}
                    onDataTypeClick={(dataType) => filterDataType(dataType)}
                    onPreviewClick={() => {
                        setData(observable);
                        setVisible(true);
                    }}
                />
            ),
        },
        {
            title: i('case.list.table.columns.details'),
            className: 'case-list__column-details',
            hidden: !organisations.some(({ links }) => links.length) || !!alertId,
            render: ({ extraData }): ReactElement => (
                <MultipleDisplayEntityInfo
                    className="case-list__details"
                    data={[
                        {
                            label: i('sharing.title'),
                            count: extraData?.shareCount || 0,
                        },
                    ]}
                />
            ),
        },
        {
            title: [
                { label: i('common.dates') },
                { label: 'S.', sortField: 'startDate', hide: !showDates.startDate },
                { label: 'C.', sortField: '_createdAt', hide: !showDates.createdAt },
                { label: 'U.', sortField: '_updatedAt', hide: !showDates.updatedAt },
            ],
            optionalRender: DisplayDates,
            className: 'observable-list__column-date',
            render: ({ _createdAt = 0, startDate = 0, _updatedAt = 0 }, { date: filterDate }): ReactElement => (
                <FormatMultipleDate
                    dates={[
                        {
                            label: 'S.',
                            value: startDate,
                            onClick: (): void => filterDate(startDate, 'startDate'),
                            hide: !showDates.startDate,
                        },
                        {
                            label: 'C.',
                            value: _createdAt,
                            onClick: (): void => filterDate(_createdAt, '_createdAt'),
                            hide: !showDates.createdAt,
                        },
                        {
                            label: 'U.',
                            value: _updatedAt,
                            onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                            hide: !showDates.updatedAt,
                        },
                    ]}
                />
            ),
        },
        {
            className: 'observable-list__column-actions',
            render: (observable): ReactElement | null => {
                const actionListResult = observableActionList([observable], (count) => {
                    listRefetch();
                    count && countRefetch();
                });

                return <ActionsList actionList={actionListResult} scope="single" isIcon />;
            },
        },
    ]);

    const rowSelection = rowSelectionCustomRenderer({
        label: i('common.selected'),
    });

    const { data: describe } = useListAllFields();

    useEffect(() => {
        setDisplay?.('startDate', showDates.startDate);
        setDisplay?.('createdAt', showDates.createdAt);
        setDisplay?.('updatedAt', showDates.updatedAt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDates]);

    useEffect(() => {
        setDisplay?.('tags', showTitle.tags);
        setDisplay?.('reports', showTitle.reports);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showTitle]);

    useEffect(() => {
        const currentView = storagePage?.currentView || '';

        setDefaultDisplay?.(currentView, {
            tags: showTitle.tags,
            reports: showTitle.reports,
            startDate: showDates.startDate,
            createdAt: showDates.createdAt,
            updatedAt: showDates.updatedAt,
        });
    }, [storagePage, showTitle, showDates, setDefaultDisplay]);

    useEffect(() => {
        setAutoRefresh?.(localAutoRefresh);
    }, [localAutoRefresh]); // eslint-disable-line

    useEffect(() => {
        setStatsIsOpen?.(localStatsIsOpen);
    }, [localStatsIsOpen]); // eslint-disable-line

    const setLocalFilters = (localFilters: boolean): void => {
        setLocalFilterIsOpen(localFilters);
        setFilterIsOpen?.(localFilters);
        filterManager.setEditFilters(localFilters);
    };

    useEffect(() => {
        setLocalFilterIsOpen(filterManager.editFilters);
    }, [filterManager.editFilters]); // eslint-disable-line

    useDrawer.watch(dataSource, () => {
        if (data) {
            setData(dataSource.find(({ _id }) => _id === data._id));
        }
    });

    return (
        <>
            <THList className={localStatsIsOpen ? 'with-stats' : undefined}>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {hasPermissions && caseId && (
                            <AddButton label={i('observable.sider.add')} onClick={() => setVisibleCreate(true)} />
                        )}

                        {storagePageManager && storageViewManager ? (
                            <ManageViewList
                                pageManager={storagePageManager}
                                viewManager={storageViewManager}
                                beforeCreate={(content: any): any => {
                                    filterManager.applyFilters();
                                    return { ...content, filters: filterManager.tempFilters };
                                }}
                            />
                        ) : null}
                        <SelectedInfo count={selectedKeys?.length} label={i('common.selected')} />
                        <ActionList
                            actions={observableActionList(
                                dataSource.filter((data: { _id: string }) => selectedKeys.includes(data['_id'])),
                                (count) => {
                                    listRefetch?.();
                                    table?.setSelectedKeys?.([]);
                                    if (count) {
                                        countRefetch?.();
                                    }
                                },
                            )}
                            scope="list"
                            hidden={!selectedKeys?.length}
                        />
                    </THList.Slot>
                    <THList.Slot name="NAVIGATION">
                        {!storagePage?.autoRefresh && (
                            <Badge dot={needRefresh}>
                                <THTooltip
                                    title={needRefresh ? i('common.refresh') : i('common.update-list')}
                                    placement="bottom"
                                >
                                    <THButton
                                        size="small"
                                        type={needRefresh ? 'primary' : 'default'}
                                        shape="circle"
                                        onClick={() => {
                                            listRefetch();
                                            countRefetch();
                                            setNeedRefresh(false);
                                        }}
                                    >
                                        <ICONS.SyncOutlined />
                                    </THButton>
                                </THTooltip>
                            </Badge>
                        )}
                        <ManageSwitch
                            label={i('common.auto-refresh')}
                            value={localAutoRefresh}
                            onChange={(autoRefresh): void => setLocalAutoRefresh(autoRefresh)}
                            icon={<ICONS.SyncOutlined />}
                        />

                        <ManageSwitch
                            label={i('common.stats')}
                            value={localStatsIsOpen}
                            onChange={(stats): void => setLocalStatsIsOpen(stats)}
                            icon={<ICONS.PieChartOutlined />}
                        />
                        <ManageSwitch
                            label={i('common.filters')}
                            value={localFilterIsOpen}
                            onChange={(editFilters): void => setLocalFilters(editFilters)}
                            icon={<ICONS.FilterOutlined />}
                        />
                    </THList.Slot>
                    <THList.Slot name="CONTENT">
                        {localStatsIsOpen && <ObservableStats quickFilters={quickFilters} />}
                        {(localFilterIsOpen || !!filterManager.tempFilters.length) && (
                            <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.OBSERVABLE]}>
                                <Footer filterConfig={filterManager} />
                            </THFilter>
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="_id"
                    rowSelection={hasPermissions ? rowSelection : undefined}
                    emptyPlaceholder={
                        <DisplayEmptyData spin={status === 'loading'} label={i('observable.none')}>
                            <THButton type="link" onClick={() => setVisibleCreate(true)}>
                                {i('observable.sider.add')}
                            </THButton>
                        </DisplayEmptyData>
                    }
                />

                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>

            {data && visible && (
                <ObservableDrawer
                    visible={visible}
                    onVisibleChange={setVisible}
                    data={data}
                    eventList={eventList}
                    caseId={caseId}
                    actionList={observableActionList}
                    alertId={alertId}
                    permissions={permissions}
                />
            )}
            {caseId && visibleCreate && (
                <ObservableCreate
                    visible={visibleCreate}
                    onVisibleChange={setVisibleCreate}
                    onFinish={(observable) => eventList.onCreate(observable, caseId)}
                />
            )}
        </>
    );
};
